/**
 * Highlight text-quote anchoring — the coordinate system for the "highlights as
 * data" rewrite. Pure, dependency-free, DOM-light. Fully unit-tested.
 *
 * WHY THIS EXISTS (read docs/qbanktestinterface-highlights-rewrite.md first):
 * Today a highlight is stored as a CHARACTER OFFSET into the island's
 * watermark-INCLUSIVE textContent, and painted by live rangy DOM surgery that
 * corrupts Vue's vdom. This module replaces that with a W3C-Web-Annotation-style
 * TEXT QUOTE anchor (quote + prefix + suffix + position hint + occurrence index)
 * expressed in CLEAN (watermark-stripped) text coordinates, so:
 *   - anchoring is decoupled from the anti-piracy watermark (it can change
 *     freely without shifting a single highlight), and
 *   - rendering can bake <mark> into the HTML string (done elsewhere), never
 *     mutating the live DOM.
 *
 * THE ONE INVARIANT: cleanText()/normalize() MUST be called identically on the
 * save side (describe) and the render side (match). Any divergence is the
 * classic "matches at save, misses at render" bug. Everything below — quote,
 * prefix, suffix, start/end, occ, docLen — lives in normalize(cleanText(island))
 * space, where watermark chars, tags and entity encodings DO NOT EXIST.
 *
 * SAFETY PROPERTY of match(): every returned range is a VERBATIM occurrence of
 * the stored quote. The worst case is landing on a different-but-identical span,
 * or returning null (orphan). It can never paint unrelated text and never
 * silently drops a highlight (the caller preserves orphans).
 *
 * Safari 12 / iOS 12: standalone .js — NOT reliably transpiled. No optional
 * chaining (?.), no nullish coalescing (??), no ES2020+.
 */
/* eslint-env commonjs */

// The watermark's textContent shape is exactly 8 alnum + "YourMedPass" + 8 alnum
// (see QuestionContentService::addWatermarks). This is the canonical stripper,
// matching stripWatermark() in qbanktestinterfacePage.vue.
var WATERMARK_TEXT_RE = /[0-9a-zA-Z]{8}YourMedPass[0-9a-zA-Z]{8}/g;
var WATERMARK_EXACT_RE = /^[0-9a-zA-Z]{8}YourMedPass[0-9a-zA-Z]{8}$/;

// Context window: floor guarantees real context survives a future edit that
// introduces a new duplicate; cap bounds blob size (up to 180 highlights/Q).
var CONTEXT_FLOOR = 20;
var CONTEXT_CAP = 32;

/**
 * Canonical text normalization. Case-SENSITIVE (medical units "Mg" != "mg",
 * "p53"); no Unicode NFC/NFD folding (keep raw code points so save and render
 * agree byte-for-byte); nbsp -> space; collapse whitespace runs; trim.
 */
function normalize(str) {
  if (str === null || str === undefined) return '';
  return String(str)
    .replace(/\u00a0/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/^ | $/g, '');
}

/** Remove watermark text from an already-plain string (belt-and-braces). */
function stripWatermarkText(str) {
  return String(str === null || str === undefined ? '' : str).replace(WATERMARK_TEXT_RE, '');
}

/**
 * Parse an HTML string into an INERT <body> element (no script execution, no
 * image/resource loading — so a hostile `<img onerror>` in stored content can
 * never fire). Returns the body element, or null when no DOM is available.
 */
function parseInertBody(html) {
  var raw = html === null || html === undefined ? '' : String(html);
  if (typeof DOMParser !== 'undefined') {
    try {
      var parsed = new DOMParser().parseFromString(raw, 'text/html');
      if (parsed && parsed.body) return parsed.body;
      // eslint-disable-next-line
    } catch (e) { /* fall through */ }
  }
  if (typeof document !== 'undefined' && document.implementation &&
      document.implementation.createHTMLDocument) {
    var inert = document.implementation.createHTMLDocument('');
    inert.body.innerHTML = raw;
    return inert.body;
  }
  return null;
}

function cleanTextFromHtml(html) {
  var body = parseInertBody(html);
  if (!body) {
    var raw = html === null || html === undefined ? '' : String(html);
    return normalize(stripWatermarkText(raw.replace(/<[^>]*>/g, ' ')));
  }
  removeWatermarkAnchors(body);
  return normalize(body.textContent || '');
}

/**
 * Clean text of a live island NODE (save side). Walks text nodes in document
 * order, skipping any inside a watermark <a>, so the basis matches
 * cleanTextFromHtml exactly. Also returns the mapping needed to convert a DOM
 * (node, offset) boundary into a clean-text offset.
 * Returns { text, map } where map is a list of
 *   { node, nodeStart(clean offset at node start), raw(node text) }
 * for the non-watermark text nodes, in order.
 */
function cleanTextFromNode(node) {
  var pieces = [];
  var map = [];
  var running = 0;
  walkTextNodes(node, function (tn) {
    if (isInsideWatermark(tn)) return;
    var t = tn.data || '';
    map.push({ node: tn, rawStart: running, raw: t });
    pieces.push(t);
    running += t.length;
  });
  // Normalize the CONCATENATED raw text the same way as the HTML path. Because
  // normalize collapses whitespace, we also expose the pre-normalize raw
  // concatenation so a DOM boundary maps into raw space first, then to clean.
  var rawConcat = pieces.join('');
  return { raw: rawConcat, text: normalize(rawConcat), map: map };
}

function walkTextNodes(root, cb) {
  if (!root) return;
  var stack = [root];
  while (stack.length) {
    var n = stack.pop();
    if (n.nodeType === 3) {
      cb(n);
    } else if (n.childNodes && n.childNodes.length) {
      // push in reverse so we visit in document order
      var i = n.childNodes.length;
      while (i--) stack.push(n.childNodes[i]);
    }
  }
}

function isInsideWatermark(textNode) {
  var p = textNode.parentNode;
  while (p) {
    if (p.nodeType === 1 && p.tagName === 'A') {
      var txt = p.textContent || '';
      if (WATERMARK_EXACT_RE.test(txt)) return true;
    }
    p = p.parentNode;
  }
  return false;
}

function removeWatermarkAnchors(el) {
  if (!el.getElementsByTagName) return;
  var anchors = el.getElementsByTagName('a');
  // collect first (live list mutates on removal)
  var toRemove = [];
  for (var i = 0; i < anchors.length; i++) {
    var a = anchors[i];
    if (WATERMARK_EXACT_RE.test(a.textContent || '')) toRemove.push(a);
  }
  for (var j = 0; j < toRemove.length; j++) {
    var t = toRemove[j];
    if (t.parentNode) t.parentNode.removeChild(t);
  }
}

/* ------------------------------------------------------------------ */
/* Pure occurrence / context helpers                                   */
/* ------------------------------------------------------------------ */

/** All start indices of needle in hay, including overlapping occurrences. */
function allIndicesOf(hay, needle) {
  var out = [];
  if (!needle) return out;
  var i = hay.indexOf(needle);
  while (i !== -1) {
    out.push(i);
    i = hay.indexOf(needle, i + 1);
  }
  return out;
}

/** Length of the longest common SUFFIX of two strings (capped at both lengths). */
function commonSuffixLen(a, b) {
  var i = a.length - 1;
  var j = b.length - 1;
  var n = 0;
  while (i >= 0 && j >= 0 && a.charAt(i) === b.charAt(j)) {
    n++; i--; j--;
  }
  return n;
}

/** Length of the longest common PREFIX of two strings. */
function commonPrefixLen(a, b) {
  var n = 0;
  var max = Math.min(a.length, b.length);
  while (n < max && a.charAt(n) === b.charAt(n)) n++;
  return n;
}

/* ------------------------------------------------------------------ */
/* describe (save side) / match (render side)                          */
/* ------------------------------------------------------------------ */

/**
 * Build an anchor for the clean-text range [start,end) of clean text C.
 * Returns { quote, prefix, suffix, start, end, occ, docLen } or null if the
 * range is empty/invalid. prefix/suffix are minimal-unique, floored to
 * CONTEXT_FLOOR and capped at CONTEXT_CAP.
 */
function describe(C, start, end) {
  if (typeof C !== 'string') return null;
  if (!(start >= 0) || !(end > start) || end > C.length) return null;
  var quote = C.slice(start, end);
  var occs = allIndicesOf(C, quote);
  var occ = 0;
  for (var i = 0; i < occs.length; i++) {
    if (occs[i] === start) { occ = i; break; }
  }
  var need = 0;
  if (occs.length > 1) {
    var k = 1;
    while (k <= 400) {
      var pfx = C.slice(Math.max(0, start - k), start);
      var sfx = C.slice(end, end + k);
      var count = 0;
      for (var j = 0; j < occs.length; j++) {
        var os = occs[j];
        var oe = os + quote.length;
        if (C.slice(Math.max(0, os - k), os) === pfx && C.slice(oe, oe + k) === sfx) {
          count++;
          if (count > 1) break;
        }
      }
      if (count === 1) break;
      k++;
    }
    need = k;
  }
  var ctx = Math.min(CONTEXT_CAP, Math.max(CONTEXT_FLOOR, need));
  return {
    quote: quote,
    prefix: C.slice(Math.max(0, start - ctx), start),
    suffix: C.slice(end, end + ctx),
    start: start,
    end: end,
    occ: occ,
    docLen: C.length,
  };
}

/**
 * Resolve an anchor A against the current clean text C. Returns {start,end} of
 * the correct occurrence, or null (orphan). Every non-null result is a verbatim
 * occurrence of A.quote — see the SAFETY PROPERTY above.
 */
function match(C, A) {
  if (typeof C !== 'string' || !A || typeof A.quote !== 'string' || A.quote === '') {
    return null;
  }
  var qlen = A.quote.length;
  var docLen = typeof A.docLen === 'number' ? A.docLen : C.length;

  // Fast path: document unchanged since save.
  if (C.length === docLen &&
      A.start >= 0 && A.end === A.start + qlen &&
      C.slice(A.start, A.end) === A.quote) {
    return { start: A.start, end: A.end };
  }

  var occs = allIndicesOf(C, A.quote);
  if (occs.length === 0) return null;
  if (occs.length === 1) return { start: occs[0], end: occs[0] + qlen };

  var prefix = typeof A.prefix === 'string' ? A.prefix : '';
  var suffix = typeof A.suffix === 'string' ? A.suffix : '';

  // Context scoring.
  var bestScore = -1;
  var ties = [];
  for (var i = 0; i < occs.length; i++) {
    var s = occs[i];
    var e = s + qlen;
    var score = commonSuffixLen(C.slice(0, s), prefix) + commonPrefixLen(C.slice(e), suffix);
    if (score > bestScore) { bestScore = score; ties = [s]; }
    else if (score === bestScore) { ties.push(s); }
  }
  if (ties.length === 1) return { start: ties[0], end: ties[0] + qlen };

  // Position-hint tiebreak (scaled if the document grew/shrank).
  var hint = Math.round((typeof A.start === 'number' ? A.start : 0) * C.length / Math.max(1, docLen));
  var pick = ties[0];
  var pd = Math.abs(ties[0] - hint);
  var posTies = [ties[0]];
  for (var t = 1; t < ties.length; t++) {
    var d = Math.abs(ties[t] - hint);
    if (d < pd) { pd = d; pick = ties[t]; posTies = [ties[t]]; }
    else if (d === pd) { posTies.push(ties[t]); }
  }
  if (posTies.length === 1) return { start: pick, end: pick + qlen };

  // Deterministic final fallback: the occ-th occurrence.
  if (typeof A.occ === 'number' && A.occ >= 0 && A.occ < occs.length) {
    return { start: occs[A.occ], end: occs[A.occ] + qlen };
  }
  return { start: pick, end: pick + qlen };
}

/* ------------------------------------------------------------------ */
/* Rendering — string injection (bake <mark> into the island HTML)     */
/* ------------------------------------------------------------------ */

/**
 * Map the island's clean text to its source (textNode, offset) positions, using
 * the SAME normalization as cleanText* (nbsp->space, whitespace-collapse, trim;
 * watermark <a> runs skipped). Returns { text, chars, nodes } where
 * chars[i] = { ni, offset } is the source of clean char i (a collapsed-space
 * clean char maps to the FIRST whitespace char of its run), ni indexes nodes[].
 */
function buildCleanCharMap(root) {
  var nodes = [];
  walkTextNodes(root, function (tn) {
    if (!isInsideWatermark(tn)) nodes.push(tn);
  });
  var text = '';
  var chars = [];
  var sawContent = false;
  var wsPending = null; // { ni, offset } of the first whitespace in a run
  for (var ni = 0; ni < nodes.length; ni++) {
    var d = nodes[ni].data || '';
    for (var oi = 0; oi < d.length; oi++) {
      var ch = d.charAt(oi);
      var isWs = ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r' ||
                 ch === '\f' || ch === '\u00a0' || /\s/.test(ch);
      if (isWs) {
        if (sawContent && wsPending === null) wsPending = { ni: ni, offset: oi };
      } else {
        if (wsPending !== null) {
          text += ' ';
          chars.push(wsPending);
          wsPending = null;
        }
        text += ch;
        chars.push({ ni: ni, offset: oi });
        sawContent = true;
      }
    }
  }
  // a trailing whitespace run is trimmed (wsPending dropped)
  return { text: text, chars: chars, nodes: nodes };
}

/**
 * Flatten a node's overlapping coverage intervals into disjoint colored
 * segments. Overlap policy: color = last-applied wins (highest `order`); ids =
 * union of every highlight covering the segment (so click-to-delete can find
 * them all). Adjacent segments with identical color+ids are merged.
 */
function segmentIntervals(intervals) {
  var cutSet = {};
  var i;
  for (i = 0; i < intervals.length; i++) {
    cutSet[intervals[i].lo] = true;
    cutSet[intervals[i].hi] = true;
  }
  var cuts = [];
  for (var c in cutSet) {
    if (Object.prototype.hasOwnProperty.call(cutSet, c)) cuts.push(parseInt(c, 10));
  }
  cuts.sort(function (a, b) { return a - b; });

  var segs = [];
  for (var k = 0; k < cuts.length - 1; k++) {
    var a = cuts[k];
    var b = cuts[k + 1];
    var winner = null;
    var ids = [];
    var seen = {};
    for (var j = 0; j < intervals.length; j++) {
      var iv = intervals[j];
      if (iv.lo <= a && iv.hi >= b) {
        if (winner === null || iv.order > winner.order) winner = iv;
        if (iv.id !== '' && !seen[iv.id]) { seen[iv.id] = true; ids.push(iv.id); }
      }
    }
    if (winner !== null) segs.push({ s: a, e: b, color: winner.color, ids: ids });
  }

  var merged = [];
  for (var m = 0; m < segs.length; m++) {
    var last = merged.length ? merged[merged.length - 1] : null;
    if (last && last.e === segs[m].s && last.color === segs[m].color &&
        last.ids.join(',') === segs[m].ids.join(',')) {
      last.e = segs[m].e;
    } else {
      merged.push({ s: segs[m].s, e: segs[m].e, color: segs[m].color, ids: segs[m].ids.slice() });
    }
  }
  return merged;
}

/**
 * Wrap disjoint [s,e) segments of a single text node in <mark> elements.
 * Processes highest-offset-first so earlier splits never invalidate lower
 * offsets. Each mark stays inside the text node's own parent, so the result is
 * always well-formed regardless of surrounding table/inline structure.
 */
function wrapNodeSegments(doc, node, segs) {
  if (!node || !node.parentNode || !segs || !segs.length) return;
  var ordered = segs.slice().sort(function (a, b) { return b.s - a.s; });
  var head = node;
  for (var i = 0; i < ordered.length; i++) {
    var seg = ordered[i];
    var len = (head.data || '').length;
    var e = Math.min(seg.e, len);
    var s = seg.s;
    if (!(e > s) || s < 0 || s > len) continue;
    if (e < len) head.splitText(e); // tail becomes a separate node (ref dropped)
    var target = s > 0 ? head.splitText(s) : head;
    var mark = doc.createElement('mark');
    mark.className = seg.color;
    if (seg.ids.length) mark.setAttribute('data-hl-ids', seg.ids.join(','));
    var parent = target.parentNode;
    if (parent) {
      parent.insertBefore(mark, target);
      mark.appendChild(target);
    }
    // `head` is now [0,s) for the next (lower) segment.
  }
}

/**
 * Bake highlight <mark> tags into the island HTML string. `ranges` are in the
 * CLEAN-text coordinate space of cleanTextFromHtml(html):
 *   [{ start, end, color: 'marker-orange', id: 'uuid' }, ...]
 * Returns the HTML with marks inserted, split per text-run so a mark never
 * crosses a tag / table-cell / watermark boundary. Overlaps are flattened
 * (last-color-wins, union of ids). Purely string in -> string out; the caller
 * hands the result to v-html so Vue owns the marks — no live-DOM surgery.
 */
function injectMarks(html, ranges) {
  var body = parseInertBody(html);
  if (!body) return html === null || html === undefined ? '' : String(html);
  var doc = body.ownerDocument;
  var map = buildCleanCharMap(body);
  var chars = map.chars;
  var nodes = map.nodes;

  var perNode = {};
  var list = ranges || [];
  for (var r = 0; r < list.length; r++) {
    var rg = list[r];
    if (!rg) continue;
    var cs = rg.start;
    var ce = rg.end;
    if (!(cs >= 0) || !(ce > cs) || ce > chars.length) continue;
    var sPos = chars[cs];
    var ePos = chars[ce - 1];
    if (!sPos || !ePos) continue;
    var color = rg.color ? String(rg.color) : 'marker-yellow';
    var id = rg.id !== undefined && rg.id !== null ? String(rg.id) : '';
    for (var ni = sPos.ni; ni <= ePos.ni; ni++) {
      var lo = ni === sPos.ni ? sPos.offset : 0;
      var hi = ni === ePos.ni ? ePos.offset + 1 : (nodes[ni].data || '').length;
      if (hi > lo) {
        if (!perNode[ni]) perNode[ni] = [];
        perNode[ni].push({ lo: lo, hi: hi, color: color, id: id, order: r });
      }
    }
  }

  for (var key in perNode) {
    if (!Object.prototype.hasOwnProperty.call(perNode, key)) continue;
    var idx = parseInt(key, 10);
    wrapNodeSegments(doc, nodes[idx], segmentIntervals(perNode[key]));
  }
  return body.innerHTML;
}

/**
 * Wrap disjoint [s,e) segments of a single text node in <span> annotation
 * elements — the annotation twin of wrapNodeSegments. Identical splitText
 * logic; emits <span class="user-annotation" data-annotation-id="ID"> where ID
 * is the winning (last-applied) annotation id carried in seg.color. Exactly ONE
 * data-annotation-id per segment (no id-union) so the existing exact-match
 * `[data-annotation-id="X"]` selectors keep working.
 */
function wrapNodeSegmentsSpan(doc, node, segs) {
  if (!node || !node.parentNode || !segs || !segs.length) return;
  var ordered = segs.slice().sort(function (a, b) { return b.s - a.s; });
  var head = node;
  for (var i = 0; i < ordered.length; i++) {
    var seg = ordered[i];
    var len = (head.data || '').length;
    var e = Math.min(seg.e, len);
    var s = seg.s;
    if (!(e > s) || s < 0 || s > len) continue;
    if (e < len) head.splitText(e);
    var target = s > 0 ? head.splitText(s) : head;
    var span = doc.createElement('span');
    span.className = 'user-annotation';
    if (seg.color) span.setAttribute('data-annotation-id', seg.color);
    var parent = target.parentNode;
    if (parent) {
      parent.insertBefore(span, target);
      span.appendChild(target);
    }
  }
}

/**
 * Bake annotation <span> tags into an HTML string. `ranges` are in the CLEAN
 * text coordinate space of cleanTextFromHtml(html):
 *   [{ start, end, id: 'ann_123' }, ...]
 * Mirrors injectMarks (per-text-run splitting, overlap flattening) but the
 * winning attribute is a single data-annotation-id (last-applied wins), not an
 * id union. Because injectMarks preserves the clean text exactly, this is safe
 * to run as a SECOND pass over already-marked HTML using the same coordinates:
 *   injectAnnotationSpans(injectMarks(content, hl), ann)
 * Purely string in -> string out; Vue owns the spans — no live-DOM surgery.
 */
function injectAnnotationSpans(html, ranges) {
  var body = parseInertBody(html);
  if (!body) return html === null || html === undefined ? '' : String(html);
  var doc = body.ownerDocument;
  var map = buildCleanCharMap(body);
  var chars = map.chars;
  var nodes = map.nodes;

  var perNode = {};
  var list = ranges || [];
  for (var r = 0; r < list.length; r++) {
    var rg = list[r];
    if (!rg) continue;
    var cs = rg.start;
    var ce = rg.end;
    if (!(cs >= 0) || !(ce > cs) || ce > chars.length) continue;
    var sPos = chars[cs];
    var ePos = chars[ce - 1];
    if (!sPos || !ePos) continue;
    var id = rg.id !== undefined && rg.id !== null ? String(rg.id) : '';
    if (id === '') continue;
    for (var ni = sPos.ni; ni <= ePos.ni; ni++) {
      var lo = ni === sPos.ni ? sPos.offset : 0;
      var hi = ni === ePos.ni ? ePos.offset + 1 : (nodes[ni].data || '').length;
      if (hi > lo) {
        if (!perNode[ni]) perNode[ni] = [];
        // Carry the id in BOTH slots: segmentIntervals picks the highest-order
        // `color` as the winner (last-applied annotation), which becomes the
        // single data-annotation-id.
        perNode[ni].push({ lo: lo, hi: hi, color: id, id: id, order: r });
      }
    }
  }

  for (var key in perNode) {
    if (!Object.prototype.hasOwnProperty.call(perNode, key)) continue;
    var idx = parseInt(key, 10);
    wrapNodeSegmentsSpan(doc, nodes[idx], segmentIntervals(perNode[key]));
  }
  return body.innerHTML;
}

/* ------------------------------------------------------------------ */
/* Save side (create an anchor) + migration (legacy blob -> anchor)    */
/* ------------------------------------------------------------------ */

/**
 * Like normalize() but trims only the LEADING space. Used to measure a clean
 * PREFIX length that lines up with the full normalize()'d text (which trims
 * BOTH ends): a prefix's trailing space is a real single space in the full text.
 */
function normLeadTrim(str) {
  if (str === null || str === undefined) return '';
  return String(str).replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').replace(/^ /, '');
}

/** Nearest exact occurrence of `needle` in `hay` to `pos`; -1 if none. */
function snapToNearest(hay, needle, pos) {
  var occs = allIndicesOf(hay, needle);
  if (!occs.length) return -1;
  var best = occs[0];
  var bd = Math.abs(occs[0] - pos);
  for (var i = 1; i < occs.length; i++) {
    var d = Math.abs(occs[i] - pos);
    if (d < bd) { bd = d; best = occs[i]; }
  }
  return best;
}

/**
 * Clean-text offset of a DOM point (node, offset) within an island: the length
 * of the normalized, watermark-stripped text from the island start up to the
 * point. Works for any container type via a cloned sub-range.
 */
function cleanOffsetOfPoint(island, node, offset) {
  var doc = (island && island.ownerDocument) || (typeof document !== 'undefined' ? document : null);
  if (!doc || !doc.createRange) return 0;
  var r = doc.createRange();
  r.setStart(island, 0);
  r.setEnd(node, offset);
  var div = doc.createElement('div');
  div.appendChild(r.cloneContents());
  removeWatermarkAnchors(div);
  return normLeadTrim(div.textContent || '').length;
}

/**
 * SAVE PATH: turn a live DOM selection Range (inside an island) into an anchor.
 * The endpoints are mapped to clean offsets and reconciled against the exact
 * selected text, so a selection with leading/trailing whitespace, or one that
 * straddles a tag/watermark, still yields the precise quote. Returns null when
 * nothing anchorable is selected.
 */
function describeFromSelection(island, range) {
  if (!island || !range) return null;
  var cleanText = cleanTextFromNode(island).text;
  var start, end;
  try {
    start = cleanOffsetOfPoint(island, range.startContainer, range.startOffset);
    end = cleanOffsetOfPoint(island, range.endContainer, range.endOffset);
    // eslint-disable-next-line
  } catch (e) {
    return null;
  }
  if (end < start) { var tmp = start; start = end; end = tmp; }
  var selText = normalize(stripWatermarkText(range.toString ? range.toString() : ''));
  if (!selText) return null;
  if (cleanText.slice(start, start + selText.length) !== selText) {
    var snapped = snapToNearest(cleanText, selText, start);
    if (snapped < 0) return null;
    start = snapped;
  }
  return describe(cleanText, start, start + selText.length);
}

/**
 * MIGRATION PATH: derive an anchor from a legacy offset entry. `oldStart`/
 * `oldEnd` are rangy character offsets into the island's RAW (un-normalized,
 * watermark-INCLUSIVE) textContent — the legacy coordinate system. We strip the
 * watermark + normalize to recover the clean quote and its clean-text position,
 * then describe(). Returns null when the range can't be recovered.
 */
function anchorFromOldEntry(island, oldStart, oldEnd, color, id) {
  if (!island) return null;
  var raw = island.textContent || '';
  oldStart = Number(oldStart);
  oldEnd = Number(oldEnd);
  if (!(oldStart >= 0) || !(oldEnd > oldStart) || oldEnd > raw.length) return null;
  var quote = normalize(stripWatermarkText(raw.slice(oldStart, oldEnd)));
  if (!quote) return null;
  var cleanText = cleanTextFromNode(island).text;
  var start = normLeadTrim(stripWatermarkText(raw.slice(0, oldStart))).length;
  if (cleanText.slice(start, start + quote.length) !== quote) {
    var snapped = snapToNearest(cleanText, quote, start);
    if (snapped < 0) return null;
    start = snapped;
  }
  var a = describe(cleanText, start, start + quote.length);
  if (!a) return null;
  a.color = color ? String(color) : 'marker-yellow';
  a.id = id !== undefined && id !== null ? String(id) : '';
  return a;
}

/**
 * Raw (watermark-INCLUSIVE, un-normalized) clean... no: this is the LEGACY
 * rangy coordinate — Range.toString() length from the island start to the
 * point, counting watermark chars. Used to dual-write a legacy blob entry so
 * old clients keep rendering a highlight created by the new path.
 */
function rawOffsetOfPoint(island, node, offset) {
  var doc = (island && island.ownerDocument) || (typeof document !== 'undefined' ? document : null);
  if (!doc || !doc.createRange) return 0;
  var r = doc.createRange();
  r.setStart(island, 0);
  r.setEnd(node, offset);
  return r.toString().length;
}

/**
 * Build a legacy rangy blob entry `start$end$id$class$container` (raw
 * watermark-inclusive offsets) for a live selection inside an island. Returns
 * null for an empty range. The blob header ('type:textContent|') is added by
 * the caller when joining entries.
 */
function legacyEntryFromSelection(island, range, className, id) {
  if (!island || !range) return null;
  var s, e;
  try {
    s = rawOffsetOfPoint(island, range.startContainer, range.startOffset);
    e = rawOffsetOfPoint(island, range.endContainer, range.endOffset);
    // eslint-disable-next-line
  } catch (ex) {
    return null;
  }
  if (e < s) { var t = s; s = e; e = t; }
  if (!(e > s)) return null;
  return s + '$' + e + '$' + id + '$' + className + '$' + island.id;
}

/**
 * Resolve a list of anchors (already filtered to one island) to clean-text
 * ranges ready for injectMarks: [{start,end,color,id}]. Orphaned anchors (quote
 * no longer present) are silently skipped (never rendered onto wrong text).
 */
function resolveAnchorsToRanges(rawHtml, anchors) {
  var out = [];
  if (!anchors || !anchors.length) return out;
  var clean = cleanTextFromHtml(rawHtml);
  for (var i = 0; i < anchors.length; i++) {
    var a = anchors[i];
    var m = match(clean, a);
    if (m) out.push({ start: m.start, end: m.end, color: a.color ? a.color : 'marker-yellow', id: a.id });
  }
  return out;
}

module.exports = {
  CONTEXT_FLOOR: CONTEXT_FLOOR,
  CONTEXT_CAP: CONTEXT_CAP,
  WATERMARK_TEXT_RE: WATERMARK_TEXT_RE,
  normalize: normalize,
  stripWatermarkText: stripWatermarkText,
  cleanTextFromHtml: cleanTextFromHtml,
  cleanTextFromNode: cleanTextFromNode,
  allIndicesOf: allIndicesOf,
  commonSuffixLen: commonSuffixLen,
  commonPrefixLen: commonPrefixLen,
  describe: describe,
  match: match,
  parseInertBody: parseInertBody,
  buildCleanCharMap: buildCleanCharMap,
  segmentIntervals: segmentIntervals,
  injectMarks: injectMarks,
  injectAnnotationSpans: injectAnnotationSpans,
  cleanOffsetOfPoint: cleanOffsetOfPoint,
  describeFromSelection: describeFromSelection,
  anchorFromOldEntry: anchorFromOldEntry,
  rawOffsetOfPoint: rawOffsetOfPoint,
  legacyEntryFromSelection: legacyEntryFromSelection,
  resolveAnchorsToRanges: resolveAnchorsToRanges,
};
